import { Component, Inject, OnDestroy, ViewEncapsulation } from '@angular/core';
import { ClusterDetailsGQL, ClusterFieldsFragment, DeleteClusterGQL } from "@/core/graphql/graphql";
import { catchError, filter, first, map, switchMap } from "rxjs/operators";
import { FormControl } from "@angular/forms";
import { EMPTY, Subscription } from "rxjs";
import { FeaturedClustersService } from "@/core/services/featured-clusters.service";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { ConfirmDialogComponent } from "@/shared/components/confirm-dialog/confirm-dialog.component";
import { CosmosDialogService, CosmosDialogStyle } from "@/core/services/cosmos-dialog.service";
import { ConfirmDialogModel } from "@/shared/components/confirm-dialog/confirm-dialog.model";
import { ToastService, ToastType } from '@/core/services/toast.service';
import { ClusterDialog } from "@/modules/clusters/components/cluster-details-modal/cluster-dialog.model";

@Component({
  selector: 'cosmos-cluster-details-modal',
  templateUrl: './cluster-details-modal.component.html',
  styleUrls: ['./cluster-details-modal.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class ClusterDetailsModalComponent implements OnDestroy {
  public _cluster: ClusterFieldsFragment;
  public isFeaturedLoading: boolean = false;
  public isFeatured: FormControl;
  public _showPublic = true;
  private readonly _subscription: Subscription = new Subscription();

  public constructor(
    private readonly _modalRef: MatDialogRef<ClusterDetailsModalComponent>,
    private readonly _featuredClustersService: FeaturedClustersService,
    private readonly _clusterDetailsGql: ClusterDetailsGQL,
    private readonly _deleteClusterGql: DeleteClusterGQL,
    private readonly _dialog: CosmosDialogService,
    private readonly _toastService: ToastService,
    @Inject(MAT_DIALOG_DATA) private readonly data: { elementId: number },
  ) {
    this._subscription.add(
      this._featuredClustersService._loadingObservable$
        .subscribe(next => this.isFeaturedLoading = next),
    );

    this._clusterDetailsGql.fetch({ id: this.data.elementId }).pipe(
      catchError((error) => {
        this._toastService.raiseMessage(error, ToastType.Error);
        return EMPTY;
      }),
    ).subscribe(next => {
      this._cluster = next.data.cluster;
      this.setupFormControl();
    });
  }

  public closeDetails(data?: ClusterDialog): void {
    this._modalRef.close(data);
  }

  public deleteCluster(): void {
    const data: ClusterDialog = {
      refreshData: true,
    };

    const dialogRef = this._dialog.open<ConfirmDialogComponent, ConfirmDialogModel>(ConfirmDialogComponent, {
      data: {
        dialogMessage: 'Do you want to delete this cluster?',
        confirmText: 'Confirm',
        cancelText: 'Cancel',
      },
      style: CosmosDialogStyle.Gray,
    });

    dialogRef.afterClosed().pipe(
      first(),
      filter(data => !!data),
      switchMap(() => {
        return this._deleteClusterGql.mutate({
          ids: [this._cluster.id],
        });
      }),
    ).subscribe((_) => {
      this.closeDetails(data);
      this._toastService.raiseMessage("Cluster has been deleted", ToastType.Warning );
    });
  }

  public showPrivateElements(): void {
    const dialogRef = this._dialog.open<ConfirmDialogComponent, ConfirmDialogModel>(ConfirmDialogComponent, {
      data: {
        dialogMessage: 'Do you want to show private elements of this cluster?',
        confirmText: 'Confirm',
        cancelText: 'Cancel',
      },
      style: CosmosDialogStyle.Gray,
    });
    dialogRef.afterClosed().pipe(
      first(),
      filter(data => !!data),
    ).subscribe(_ => this._showPublic = false);
  }

  public ngOnDestroy(): void {
    this._subscription.unsubscribe();
  }

  private setupFormControl(): void {
    this._subscription.add(
      this._featuredClustersService.getFeaturedClusters().pipe(
        map((clusters) => clusters.map(cluster => cluster.id)),
      ).subscribe((elementIds: string[]) => {
        this.isFeatured = new FormControl(elementIds.includes(this._cluster.id, 0));
        this.handleFormControl();
      }),
    );
  }

  private handleFormControl(): void {
    if (this.isFeatured) {
      this._subscription.add(
        this.isFeatured.valueChanges.subscribe(next => {
          next
            ? this._featuredClustersService.addFeaturedCluster(this._cluster.id)
            : this._featuredClustersService.removeFeaturedCluster(this._cluster.id);
        }),
      );
    }
  }
}
